import { atomWithStorage } from "jotai/utils"

import {
  EAdDisplayFormat,
  EAdLanguage,
  EAdSortOrder,
  EMarketTarget,
  ENiche,
  EPublisherPlatform,
} from "@aix/vidmax-shared/src/constants/enums"
import type { IScrapedContent } from "@aix/vidmax-shared/src/constants/prismaTypings"

/** Masonry zoom (1 = zoomed out / smallest tiles / most columns, 5 = zoomed in / biggest / fewest). Persisted. */
export const adLibraryZoomAtom = atomWithStorage<number>("vidmax_adLibrary_zoom", 3)
export const MIN_ZOOM = 1
export const MAX_ZOOM = 5
const TILE_HEIGHT_BY_ZOOM_REM = [10, 12, 14, 17, 20] as const
export const zoomToTileHeightRem = (zoom: number): number =>
  TILE_HEIGHT_BY_ZOOM_REM[Math.min(Math.max(Math.round(zoom), MIN_ZOOM), MAX_ZOOM) - MIN_ZOOM] ?? 14

/**
 * URL-synced Ad Library filter state (TanStack `useSearch`). Every value is a **string** (arrays
 * are comma-joined, booleans are "true"/"false", numbers are stringified). This is deliberate:
 * TanStack merges every route's validated search into one global `FullSearchSchema`, and a shared
 * navigation adapter feeds `location.search` straight into `URLSearchParams`, which only accepts
 * string values — introducing typed arrays/numbers here would break it app-wide. The page parses
 * these strings back into typed enum arrays / numbers with the helpers below.
 */
export interface IAdLibrarySearch {
  /** Selected ad shown in the detail lightbox. Admin pages keep detail state in search params. */
  adId?: string
  q?: string
  sort?: string
  live?: string
  formats?: string
  platforms?: string
  niches?: string
  markets?: string
  languages?: string
  videoMin?: string
  videoMax?: string
  runMinDays?: string
  runMaxDays?: string
  startDate?: string
  endDate?: string
  boardId?: string
  favorites?: string
  /** Per-user suppression view. Visible is the default and therefore omitted. */
  visibility?: string
  /** Review queue view. All is the default and therefore omitted. */
  review?: string
  /** Duplicate presentation mode. Collapsed is the default. */
  duplicates?: string
  /** Opens the competitor-brand freshness and suppression drawer. */
  brandManager?: string
  /** Opens the duplicate-cluster detail drawer/dialog for this representative ad. */
  clusterAdId?: string
  /** Single content-source id (Brand facet). Multi-select is deferred until the backend adds `sourceIds[]`. */
  brandId?: string
}

const enumValues = <T extends string>(e: Record<string, T>): Set<string> => new Set(Object.values(e))

const FORMAT_VALUES = enumValues(EAdDisplayFormat)
const PLATFORM_VALUES = enumValues(EPublisherPlatform)
const NICHE_VALUES = enumValues(ENiche)
const MARKET_VALUES = enumValues(EMarketTarget)
const LANGUAGE_VALUES = enumValues(EAdLanguage)
const SORT_VALUES = enumValues(EAdSortOrder)
const VISIBILITY_VALUES = new Set(["visible", "hidden", "all"])
const REVIEW_VALUES = new Set(["all", "unreviewed"])
const DUPLICATE_VALUES = new Set(["collapsed", "all", "duplicates_only"])

const asString = (raw: unknown): string | undefined => {
  if (typeof raw !== "string") return undefined
  const trimmed = raw.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

/** Sanitize a CSV of enum values to a CSV of only the valid ones (undefined when none survive). */
const sanitizeCsv = (raw: unknown, valid: Set<string>): string | undefined => {
  const str = asString(raw)
  if (!str) return undefined
  const kept = str.split(",").filter((token) => valid.has(token))
  return kept.length > 0 ? kept.join(",") : undefined
}

const sanitizeBool = (raw: unknown): string | undefined => {
  if (raw === "true" || raw === true) return "true"
  if (raw === "false" || raw === false) return "false"
  return undefined
}

const sanitizeNumber = (raw: unknown): string | undefined => {
  const n = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : NaN
  return Number.isFinite(n) ? String(n) : undefined
}

/**
 * TanStack `validateSearch` for `/admin/ad-library`. Defensive: unknown/garbage params are dropped,
 * never thrown, so a hand-edited or stale URL still renders the feed. Returns only strings.
 */
export const validateAdLibrarySearch = (raw: Record<string, unknown>): IAdLibrarySearch => {
  const search: IAdLibrarySearch = {}

  const adId = asString(raw.adId)
  if (adId) search.adId = adId
  const q = asString(raw.q)
  if (q) search.q = q
  const sort = typeof raw.sort === "string" && SORT_VALUES.has(raw.sort) ? raw.sort : undefined
  if (sort) search.sort = sort
  const live = sanitizeBool(raw.live)
  if (live) search.live = live
  const formats = sanitizeCsv(raw.formats, FORMAT_VALUES)
  if (formats) search.formats = formats
  const platforms = sanitizeCsv(raw.platforms, PLATFORM_VALUES)
  if (platforms) search.platforms = platforms
  const niches = sanitizeCsv(raw.niches, NICHE_VALUES)
  if (niches) search.niches = niches
  const markets = sanitizeCsv(raw.markets, MARKET_VALUES)
  if (markets) search.markets = markets
  const languages = sanitizeCsv(raw.languages, LANGUAGE_VALUES)
  if (languages) search.languages = languages
  const videoMin = sanitizeNumber(raw.videoMin)
  if (videoMin) search.videoMin = videoMin
  const videoMax = sanitizeNumber(raw.videoMax)
  if (videoMax) search.videoMax = videoMax
  const runMinDays = sanitizeNumber(raw.runMinDays)
  if (runMinDays) search.runMinDays = runMinDays
  const runMaxDays = sanitizeNumber(raw.runMaxDays)
  if (runMaxDays) search.runMaxDays = runMaxDays
  const startDate = asString(raw.startDate)
  if (startDate) search.startDate = startDate
  const endDate = asString(raw.endDate)
  if (endDate) search.endDate = endDate
  const boardId = asString(raw.boardId)
  if (boardId) search.boardId = boardId
  const favorites = sanitizeBool(raw.favorites)
  if (favorites === "true") search.favorites = favorites
  const visibility =
    typeof raw.visibility === "string" && VISIBILITY_VALUES.has(raw.visibility) ? raw.visibility : undefined
  if (visibility && visibility !== "visible") search.visibility = visibility
  const review = typeof raw.review === "string" && REVIEW_VALUES.has(raw.review) ? raw.review : undefined
  if (review && review !== "all") search.review = review
  const duplicates =
    typeof raw.duplicates === "string" && DUPLICATE_VALUES.has(raw.duplicates) ? raw.duplicates : undefined
  if (duplicates && duplicates !== "collapsed") search.duplicates = duplicates
  const brandManager = sanitizeBool(raw.brandManager)
  if (brandManager === "true") search.brandManager = brandManager
  const clusterAdId = asString(raw.clusterAdId)
  if (clusterAdId) search.clusterAdId = clusterAdId
  const brandId = asString(raw.brandId)
  if (brandId) search.brandId = brandId

  return search
}

// ── Parse (URL strings → typed) ──────────────────────────────────────────────

export const parseSort = (raw: string | undefined): EAdSortOrder =>
  raw && SORT_VALUES.has(raw) ? (raw as EAdSortOrder) : EAdSortOrder.NEWEST

export const parseEnumCsv = <T extends string>(raw: string | undefined, valid: Set<string>): T[] => {
  if (!raw) return []
  return raw.split(",").filter((token): token is T => valid.has(token))
}

export const parseFormats = (raw: string | undefined): EAdDisplayFormat[] => parseEnumCsv(raw, FORMAT_VALUES)
export const parsePlatforms = (raw: string | undefined): EPublisherPlatform[] => parseEnumCsv(raw, PLATFORM_VALUES)
export const parseNiches = (raw: string | undefined): ENiche[] => parseEnumCsv(raw, NICHE_VALUES)
export const parseMarkets = (raw: string | undefined): EMarketTarget[] => parseEnumCsv(raw, MARKET_VALUES)
export const parseLanguages = (raw: string | undefined): EAdLanguage[] => parseEnumCsv(raw, LANGUAGE_VALUES)

export const parseBool = (raw: string | undefined): boolean | undefined =>
  raw === "true" ? true : raw === "false" ? false : undefined

export const parseNum = (raw: string | undefined): number | undefined => {
  if (raw === undefined) return undefined
  const n = Number(raw)
  return Number.isFinite(n) ? n : undefined
}

// ── Serialize (typed → URL string) ───────────────────────────────────────────

export const serializeCsv = (values: string[]): string | undefined => (values.length > 0 ? values.join(",") : undefined)

export const serializeBool = (value: boolean | undefined): string | undefined =>
  value === undefined ? undefined : value ? "true" : "false"

// ── Human labels ─────────────────────────────────────────────────────────────

export const FORMAT_LABELS: Record<EAdDisplayFormat, string> = {
  [EAdDisplayFormat.VIDEO]: "Video",
  [EAdDisplayFormat.IMAGE]: "Image",
  [EAdDisplayFormat.CAROUSEL]: "Carousel",
  [EAdDisplayFormat.DCO]: "DCO",
  [EAdDisplayFormat.DPA]: "DPA",
  [EAdDisplayFormat.MULTI_IMAGES]: "Multi-image",
  [EAdDisplayFormat.MULTI_VIDEOS]: "Multi-video",
  [EAdDisplayFormat.MULTI_MEDIAS]: "Multi-media",
  [EAdDisplayFormat.EVENT]: "Event",
  [EAdDisplayFormat.TEXT]: "Text",
  [EAdDisplayFormat.PAGE_LIKE]: "Page like",
}

export const PLATFORM_LABELS: Record<EPublisherPlatform, string> = {
  [EPublisherPlatform.FACEBOOK]: "Facebook",
  [EPublisherPlatform.INSTAGRAM]: "Instagram",
  [EPublisherPlatform.AUDIENCE_NETWORK]: "Audience Network",
  [EPublisherPlatform.MESSENGER]: "Messenger",
  [EPublisherPlatform.TIKTOK]: "TikTok",
  [EPublisherPlatform.TIKTOK_ORGANIC]: "TikTok Organic",
  [EPublisherPlatform.TIKTOK_ADS]: "TikTok Ads",
  [EPublisherPlatform.YOUTUBE]: "YouTube",
  [EPublisherPlatform.LINKEDIN]: "LinkedIn",
  [EPublisherPlatform.THREADS]: "Threads",
  [EPublisherPlatform.WHATSAPP]: "WhatsApp",
}

/** Foreplay-parity display labels for the full niche list (values are snake_case). */
export const NICHE_LABELS: Record<ENiche, string> = {
  [ENiche.ACCESSORIES]: "Accessories",
  [ENiche.ALCOHOL]: "Alcohol",
  [ENiche.APP_SOFTWARE]: "App/Software",
  [ENiche.AUTOMOTIVE]: "Automotive",
  [ENiche.BEAUTY]: "Beauty",
  [ENiche.BOOK_PUBLISHING]: "Book Publishing",
  [ENiche.BUSINESS_PROFESSIONAL]: "Business/Professional",
  [ENiche.CHARITY_NFP]: "Charity/NFP",
  [ENiche.EDUCATION]: "Education",
  [ENiche.ENTERTAINMENT]: "Entertainment",
  [ENiche.FASHION]: "Fashion",
  [ENiche.FINANCE]: "Finance",
  [ENiche.FOOD_DRINK]: "Food/Drink",
  [ENiche.GAMES]: "Games",
  [ENiche.GOVERNMENT]: "Government",
  [ENiche.HEALTH_WELLNESS]: "Health/Wellness",
  [ENiche.HOME_GARDEN]: "Home/Garden",
  [ENiche.INSURANCE]: "Insurance",
  [ENiche.JEWELRY_WATCHES]: "Jewelry/Watches",
  [ENiche.KIDS_BABY]: "Kids/Baby",
  [ENiche.MEDIA_NEWS]: "Media/News",
  [ENiche.MEDICAL]: "Medical",
  [ENiche.PETS]: "Pets",
  [ENiche.REAL_ESTATE]: "Real Estate",
  [ENiche.SERVICE_BUSINESS]: "Service Business",
  [ENiche.SPORTS_OUTDOORS]: "Sports/Outdoors",
  [ENiche.TECH]: "Tech",
  [ENiche.TRAVEL]: "Travel",
  [ENiche.OTHER]: "Other",
}

/** Display labels for the full language list (enum key = language name, value = ISO 639-1 code). */
export const LANGUAGE_LABELS: Record<EAdLanguage, string> = {
  [EAdLanguage.AFRIKAANS]: "Afrikaans",
  [EAdLanguage.ALBANIAN]: "Albanian",
  [EAdLanguage.ARABIC]: "Arabic",
  [EAdLanguage.ARMENIAN]: "Armenian",
  [EAdLanguage.AZERBAIJANI]: "Azerbaijani",
  [EAdLanguage.BAMBARA]: "Bambara",
  [EAdLanguage.BASQUE]: "Basque",
  [EAdLanguage.BENGALI]: "Bengali",
  [EAdLanguage.BOSNIAN]: "Bosnian",
  [EAdLanguage.BULGARIAN]: "Bulgarian",
  [EAdLanguage.BURMESE]: "Burmese",
  [EAdLanguage.CATALAN]: "Catalan",
  [EAdLanguage.KHMER]: "Khmer",
  [EAdLanguage.NYANJA]: "Nyanja",
  [EAdLanguage.CORSICAN]: "Corsican",
  [EAdLanguage.CROATIAN]: "Croatian",
  [EAdLanguage.CZECH]: "Czech",
  [EAdLanguage.DANISH]: "Danish",
  [EAdLanguage.DUTCH]: "Dutch",
  [EAdLanguage.ENGLISH]: "English",
  [EAdLanguage.ESTONIAN]: "Estonian",
  [EAdLanguage.FINNISH]: "Finnish",
  [EAdLanguage.FRENCH]: "French",
  [EAdLanguage.GALICIAN]: "Galician",
  [EAdLanguage.GEORGIAN]: "Georgian",
  [EAdLanguage.GERMAN]: "German",
  [EAdLanguage.GREEK]: "Greek",
  [EAdLanguage.HEBREW]: "Hebrew",
  [EAdLanguage.HINDI]: "Hindi",
  [EAdLanguage.HUNGARIAN]: "Hungarian",
  [EAdLanguage.ICELANDIC]: "Icelandic",
  [EAdLanguage.INDONESIAN]: "Indonesian",
  [EAdLanguage.ITALIAN]: "Italian",
  [EAdLanguage.JAPANESE]: "Japanese",
  [EAdLanguage.KOREAN]: "Korean",
  [EAdLanguage.LATVIAN]: "Latvian",
  [EAdLanguage.LITHUANIAN]: "Lithuanian",
  [EAdLanguage.MALAY]: "Malay",
  [EAdLanguage.NORWEGIAN]: "Norwegian",
  [EAdLanguage.PERSIAN]: "Persian",
  [EAdLanguage.POLISH]: "Polish",
  [EAdLanguage.PORTUGUESE]: "Portuguese",
  [EAdLanguage.ROMANIAN]: "Romanian",
  [EAdLanguage.RUSSIAN]: "Russian",
  [EAdLanguage.SERBIAN]: "Serbian",
  [EAdLanguage.SLOVAK]: "Slovak",
  [EAdLanguage.SLOVENIAN]: "Slovenian",
  [EAdLanguage.SPANISH]: "Spanish",
  [EAdLanguage.SWAHILI]: "Swahili",
  [EAdLanguage.SWEDISH]: "Swedish",
  [EAdLanguage.TAGALOG]: "Tagalog",
  [EAdLanguage.THAI]: "Thai",
  [EAdLanguage.TURKISH]: "Turkish",
  [EAdLanguage.UKRAINIAN]: "Ukrainian",
  [EAdLanguage.URDU]: "Urdu",
  [EAdLanguage.VIETNAMESE]: "Vietnamese",
  [EAdLanguage.CHINESE]: "Chinese",
}

// ── Foreplay-parity menu option ordering ─────────────────────────────────────

/** Format menu shows only these 4 (storage supports more display formats). */
export const FORMAT_MENU_ORDER: EAdDisplayFormat[] = [
  EAdDisplayFormat.VIDEO,
  EAdDisplayFormat.IMAGE,
  EAdDisplayFormat.CAROUSEL,
  EAdDisplayFormat.DCO,
]

/** Platform menu shows exactly these 6, in this order. */
export const PLATFORM_MENU_ORDER: EPublisherPlatform[] = [
  EPublisherPlatform.FACEBOOK,
  EPublisherPlatform.INSTAGRAM,
  EPublisherPlatform.TIKTOK_ORGANIC,
  EPublisherPlatform.TIKTOK_ADS,
  EPublisherPlatform.YOUTUBE,
  EPublisherPlatform.LINKEDIN,
]

/** Full niche list, alphabetized by label with "Other" pinned last (Foreplay parity). */
export const NICHE_MENU_ORDER: ENiche[] = [
  ...Object.values(ENiche)
    .filter((n) => n !== ENiche.OTHER)
    .sort((a, b) => NICHE_LABELS[a].localeCompare(NICHE_LABELS[b])),
  ENiche.OTHER,
]

/** Full language list, alphabetized by display label. */
export const LANGUAGE_MENU_ORDER: EAdLanguage[] = Object.values(EAdLanguage).sort((a, b) =>
  LANGUAGE_LABELS[a].localeCompare(LANGUAGE_LABELS[b]),
)

// ── Range presets (Video Length / Run Time) ──────────────────────────────────

/** A single-select range preset that maps to a (min, max) pair of feed params. */
export interface IRangePreset {
  id: string
  label: string
  min?: number
  max?: number
}

/** Video Length presets → videoDurationMin / videoDurationMax (seconds). */
export const VIDEO_LENGTH_PRESETS: IRangePreset[] = [
  { id: "under15", label: "Under 15s", max: 15 },
  { id: "15to30", label: "15–30s", min: 15, max: 30 },
  { id: "30to60", label: "30–60s", min: 30, max: 60 },
  { id: "over60", label: "Over 60s", min: 60 },
]

/** Run Time presets → runningDurationMinDays / runningDurationMaxDays (days). Foreplay's exact set. */
export const RUN_TIME_PRESETS: IRangePreset[] = [
  { id: "under7", label: "Under 7 Days", max: 7 },
  { id: "over7", label: "Over 7 Days", min: 7 },
  { id: "over15", label: "Over 15 Days", min: 15 },
  { id: "over30", label: "Over 30 Days", min: 30 },
  { id: "over90", label: "Over 3 months", min: 90 },
  { id: "over180", label: "Over 6 months", min: 180 },
  { id: "over365", label: "Over 12 months", min: 365 },
]

/** Find the preset (if any) whose bounds equal the current min/max — for reflecting URL state in the UI. */
export const matchRangePreset = (
  presets: IRangePreset[],
  min: number | undefined,
  max: number | undefined,
): IRangePreset | undefined => presets.find((p) => p.min === min && p.max === max)

export const MARKET_LABELS: Record<EMarketTarget, string> = {
  [EMarketTarget.B2B]: "B2B",
  [EMarketTarget.B2C]: "B2C",
}

export const SORT_LABELS: Record<EAdSortOrder, string> = {
  [EAdSortOrder.SAVED_NEWEST]: "Recently saved",
  [EAdSortOrder.NEWEST]: "Newest",
  [EAdSortOrder.OLDEST]: "Oldest",
  [EAdSortOrder.LONGEST_RUNNING]: "Longest running",
}

/** Title-case a raw enum value like "health/wellness" → "Health/Wellness" for niche/language chips. */
export const humanizeEnumValue = (value: string): string =>
  value
    .split(/[\s/_-]+/)
    .map((word) => (word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ")

// ── Ad-derived display helpers ───────────────────────────────────────────────

/** Brand / advertiser name for an ad — the content source name, with a safe fallback. */
export const getAdvertiserName = (ad: IScrapedContent): string =>
  ad.source?.name || ad.metadata?.platformData?.pageName || "Unknown advertiser"

/** First playable video url (hover-to-play) for an ad, if any. */
export const getAdVideoUrl = (ad: IScrapedContent): string | undefined =>
  ad.mediaUrls?.find((m) => m.type === "video")?.url

/** Best still image (poster / first image) for an ad, if any. */
export const getAdImageUrl = (ad: IScrapedContent): string | undefined => {
  const video = ad.mediaUrls?.find((m) => m.type === "video")
  if (video?.thumbnail) return video.thumbnail
  return ad.mediaUrls?.find((m) => m.type === "image")?.url ?? ad.mediaUrls?.[0]?.thumbnail
}

/** Whole-days an ad has been (or was) running, from started/ended run dates. `null` when unknown. */
export const getRunningDays = (ad: IScrapedContent): number | null => {
  if (!ad.startedRunningAt) return null
  const start = new Date(ad.startedRunningAt).getTime()
  const end = ad.endedRunningAt ? new Date(ad.endedRunningAt).getTime() : Date.now()
  if (!Number.isFinite(start) || end < start) return null
  return Math.max(0, Math.floor((end - start) / 86_400_000))
}
